import type { TelemetrySeriesPoint } from '@/features/telemetry/services/schemas';

export interface StatusSegment {
  from: Date;
  to: Date;
  /** The raw, uninterpreted status value from the backend, or null for a data gap. */
  status: string | null;
}

interface Bucket {
  ts: number;
  status: string | null;
}

/** A bucket's raw status value, normalized to a comparable string (or null). */
function normalizeStatus(raw: unknown): string | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  return String(raw);
}

function parseBuckets(points: TelemetrySeriesPoint[], key: string): Bucket[] {
  return points
    .map((point): Bucket => ({ ts: Date.parse(point.time), status: normalizeStatus(point[key]) }))
    .filter((bucket) => !Number.isNaN(bucket.ts))
    .sort((a, b) => a.ts - b.ts);
}

/**
 * Run-length-encodes a device's raw bucket-by-bucket status values into contiguous
 * segments for the status timeline — the real version of what the source prototype
 * faked with a random generator.
 *
 * Status values are never interpreted: whatever the backend sends for a bucket is the
 * segment's `status`, verbatim. A missing/null value becomes a `status: null` gap
 * segment rather than being folded into whichever segment happens to be open — a
 * genuine reporting gap must read as a gap, not as the previous status persisting. A
 * timestamp gap larger than one bucket (missing buckets entirely) closes the current
 * run the same way, leaving the hole uncovered by any segment.
 *
 * The trailing segment's `to` is clamped to `rangeTo`: `to` is an exclusive window
 * bound, so no bucket is ever stamped exactly at `rangeTo`, and without the clamp the
 * last segment would fall short of the visible range by one bucket.
 */
export function toStatusSegments(
  points: TelemetrySeriesPoint[],
  key: string,
  rangeFrom: Date,
  rangeTo: Date,
  bucketMs: number
): StatusSegment[] {
  const buckets = parseBuckets(points, key);
  const rangeToMs = rangeTo.getTime();
  const segments: StatusSegment[] = [];

  let runStart: number | null = null;
  let runStatus: string | null = null;
  let previousTs: number | null = null;

  const pushSegment = (from: number, to: number, status: string | null) => {
    if (to > from) {
      segments.push({ from: new Date(from), to: new Date(to), status });
    }
  };

  for (const bucket of buckets) {
    const isTimestampGap = previousTs !== null && bucket.ts - previousTs > bucketMs;
    const isStatusChange = runStatus !== bucket.status;

    if (runStart !== null && (isTimestampGap || isStatusChange)) {
      const closeAt = isTimestampGap ? previousTs! + bucketMs : bucket.ts;
      pushSegment(runStart, closeAt, runStatus);
      runStart = null;
    }

    if (runStart === null) {
      runStart = bucket.ts;
      runStatus = bucket.status;
    }

    previousTs = bucket.ts;
  }

  if (runStart !== null && previousTs !== null) {
    pushSegment(runStart, Math.min(previousTs + bucketMs, rangeToMs), runStatus);
  }

  return segments.filter((segment) => segment.to.getTime() > rangeFrom.getTime());
}
